import { VisitOutcome, VisitState, isVisitComplete } from '@/types/visit';
import { Moment } from 'moment-timezone';

/* The outcome to select when the visit moves to a status that requires one.
 *
 * Completed when it is viewable, otherwise the first viewable outcome that means something,
 * and None when nothing else is viewable.
 */
export const pickDefaultCompleteOutcome = (viewableVisitOutcomes: VisitOutcome[]): VisitOutcome =>
    viewableVisitOutcomes.includes(VisitOutcome.COMPLETED)
        ? VisitOutcome.COMPLETED
        : viewableVisitOutcomes.filter((outcome) => outcome !== VisitOutcome.NONE)[0] ??
          VisitOutcome.NONE;

/* Whether the visit's status carries a datetime.
 *
 * An unscheduled visit has none, and the backend infers it as inapplicable, so neither the
 * field nor the value is sent.
 */
export const visitStateHasDatetime = (state: VisitState): boolean =>
    state !== VisitState.UNSCHEDULED;

// Whether any of the fields that place the visit in time holds a value.
export const hasAnyVisitDateInfo = ({
    datetime,
    visitHasDatetime,
    tentativeDate,
    earliestFulfillment,
    latestFulfillment,
}: {
    datetime: Moment | null;
    visitHasDatetime: boolean;
    tentativeDate: Date | null;
    earliestFulfillment: Moment | null;
    latestFulfillment: Moment | null;
}): boolean =>
    (datetime !== null && visitHasDatetime) ||
    tentativeDate !== null ||
    earliestFulfillment !== null ||
    latestFulfillment !== null;

/* Whether the visit can be edited at all, given its status and the editor's permissions.
 *
 * Individual fields are still gated by their own permissions. An incomplete visit is editable
 * by anyone who can edit visits; a complete one only by an editor who may edit past an outcome.
 */
export const canEditVisitInState = ({
    state,
    canEditWithCompletedOutcome,
}: {
    state: VisitState;
    canEditWithCompletedOutcome: boolean;
}): boolean => !isVisitComplete(state) || canEditWithCompletedOutcome;
